import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  TextField,
  Grid,
  Divider,
  MenuItem,
  Autocomplete,
  Stack,
  FormControlLabel,
  Checkbox,
  Avatar,
  Tooltip,
  TablePagination,
  Pagination,
  Switch,
} from "@mui/material";
import { getFunctionalsForEmployee } from "services/api";
import { useAuth } from "context/authContext";
import MonthYearSelector from "components/DateSelector";
import LoadingScreen from "components/Loading";
import { fieldStyles } from "services/utils";
import SnackbarWrapper from "components/Snackbar";
import { saveFunctionalPerformance } from "services/api";
import { useTranslation } from "react-i18next";
import { numberTextFieldStyling } from "services/utils";
import { getFunctionalsForEmployeeQuarterly } from "services/api";

const pageSize = 2;

export default function FunctionalAssessmentPage() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2022 + 1 }, (_, i) => 2023 + i);
  const snackbarRef = useRef();
  const { employee, selectedMonth, selectedYear, quarter, isQuarterly } = useAuth();
  const [view, setView] = useState("byJob");
  const [data, setData] = useState([]);
  const [achievements, setAchievements] = useState({});
  const [loading, setLoading] = useState(false);
  const [jobFilter, setJobFilter] = useState("");
  const [kpiFilter, setKpiFilter] = useState("");
  const [jobTitles, setJobTitles] = useState([]);
  const [kpiNames, setKpiNames] = useState([]);
  const [onlyUnassessed, setOnlyUnassessed] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // number of employees (groups) per page

  useEffect(() => {
    setLoading(true);
    let functionalsForEmp;
    if (isQuarterly) {
      functionalsForEmp = getFunctionalsForEmployeeQuarterly(employee.id, quarter, selectedYear);
    } else {
      functionalsForEmp = getFunctionalsForEmployee(employee.id, selectedMonth, selectedYear);
    }

    if (employee?.id) {
      // getFunctionalsForEmployee(employee.id, selectedMonth, selectedYear)
      functionalsForEmp
        .then((data) => {
          if (data != "error") {
            const jobTitles = Array.from(new Set(data.map((row) => row[3]))); // index 3 = jobTitle
            const kpiNames = Array.from(new Set(data.map((row) => row[4]))); // index 4 = kpiName
            setJobTitles(jobTitles);
            setKpiNames(kpiNames);
            setData(data);

            const achievementsMap = {};
            data.forEach((row) => {
              const functionalId = row[0]; // index 0 = functionalId
              const empId = row[1];
              const achievement = row[5]; // index 5 = achievement
              if (achievement != null) {
                achievementsMap[functionalId + "_" + empId] = achievement;
              }
            });
            setAchievements(achievementsMap);
          }
        })
        .then((d) => {
          setLoading(false);
        });
    }
  }, [employee.id, selectedMonth, selectedYear, quarter, isQuarterly]);

  const handleViewChange = (event, newView) => {
    if (newView) setView(newView);
  };

  const handleAchievementChange = (id, value) => {
    setAchievements((prev) => ({ ...prev, [id]: value }));
  };

  const handleSaveAchievement = async (e, id) => {
    if (achievements[id] == null) return;
    const [functionalIdStr, employeeIdStr] = id.split("_");
    const functionalId = parseInt(functionalIdStr, 10);
    const employeeId = employeeIdStr;
    const index = data.findIndex((row) => row[0] === functionalId && row[1] === employeeId);
    if (index === -1) return;
    const matchingRow = data[index];
    if (!matchingRow || matchingRow[5] == achievements[id]) return;
    const achievement = achievements[id];
    const month = !isQuarterly ? selectedMonth : quarter >= 1 && quarter <= 4 ? quarter * 3 : 0;
    if (month === 0) return;
    const res = await saveFunctionalPerformance({
      achievement,
      functionalId,
      employeeId,
      // month: selectedMonth,
      month,
      year: selectedYear,
    });

    if (res != "error") {
      snackbarRef.current?.showSnackbar(t("user.functional.achievementSaved"), "success");
      const updatedData = [...data]; // shallow copy for immutability
      updatedData[index][5] = res.achievement; // update the achievement value at index 5
      setData(updatedData);
    } else {
      snackbarRef.current?.showSnackbar(t("user.functional.error"), "error");
    }
  };

  const {
    groupedByJob,
    groupedByKPI,
    paginatedJobs,
    paginatedKPIs,
    totalJobGroups,
    totalKPIGroups,
  } = useMemo(() => {
    const groupedByJob = {};
    const groupedByKPI = {};

    data?.forEach(
      ([functionalId, empId, empName, jobTitle, kpiName, achievement, target, image]) => {
        if (onlyUnassessed && achievement != null) return;
        if (view === "byJob" && jobFilter && jobFilter !== jobTitle) return;
        if (view === "byKPI" && kpiFilter && kpiFilter !== kpiName) return;

        // Group by Job
        if (!groupedByJob[jobTitle]) groupedByJob[jobTitle] = {};
        if (!groupedByJob[jobTitle][empName]) groupedByJob[jobTitle][empName] = [];
        groupedByJob[jobTitle][empName].push({
          functionalId,
          empId,
          kpiName,
          achievement,
          target,
          image,
        });

        // Group by KPI
        if (!groupedByKPI[kpiName]) groupedByKPI[kpiName] = [];
        groupedByKPI[kpiName].push({
          functionalId,
          empId,
          empName,
          jobTitle,
          achievement,
          target,
          image,
        });
      }
    );

    // Convert to arrays for pagination
    const jobEntries = Object.entries(groupedByJob);
    const kpiEntries = Object.entries(groupedByKPI);

    const paginatedJobs = jobEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const paginatedKPIs = kpiEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return {
      groupedByJob,
      groupedByKPI,
      paginatedJobs,
      paginatedKPIs,
      totalJobGroups: jobEntries.length,
      totalKPIGroups: kpiEntries.length,
    };
  }, [data, onlyUnassessed, jobFilter, kpiFilter, view, page, rowsPerPage]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>
        {t("user.functional.functionalAssessment")}
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        {/* Left: Toggle Button Group */}
        <ToggleButtonGroup value={view} exclusive onChange={handleViewChange}>
          <ToggleButton value="byJob">{t("user.functional.byJob")}</ToggleButton>
          <ToggleButton value="byKPI">{t("user.functional.byKPI")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box mt={2} width="100%" mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <Box width={{ xs: "100%", sm: "auto", minWidth: 250 }}>
          {view === "byJob" && (
            <Autocomplete
              size="small"
              value={jobFilter}
              onChange={(e, newValue) => setJobFilter(newValue || "")}
              options={["", ...jobTitles]}
              getOptionLabel={(option) => option || "All"}
              renderInput={(params) => (
                <TextField {...params} label={t("user.functional.filterByJob")} sx={fieldStyles} />
              )}
            />
          )}

          {view === "byKPI" && (
            <Autocomplete
              size="small"
              value={kpiFilter}
              onChange={(e, newValue) => setKpiFilter(newValue || "")}
              options={["", ...kpiNames]}
              getOptionLabel={(option) => option || "All"}
              renderInput={(params) => (
                <TextField {...params} label={t("user.functional.filterByKPI")} sx={fieldStyles} />
              )}
            />
          )}
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={onlyUnassessed}
              onChange={(e) => setOnlyUnassessed(e.target.checked)}
            />
          }
          label={t("user.functional.onlyUnassessed")}
        />
      </Box>

      {view === "byJob"
        ? paginatedJobs.map(([jobTitle, employees]) => (
            <Paper key={jobTitle} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" mb={1}>
                {jobTitle}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {Object.entries(employees).map(([empName, kpis]) => (
                <Box key={empName} mb={2}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Avatar
                      src={kpis[0]?.image ? `data:image/jpeg;base64,${kpis[0].image}` : undefined}
                      alt={empName}
                      sx={{ width: 32, height: 32, mr: 1 }}
                    />
                    <Typography variant="subtitle1">{empName}</Typography>
                  </Box>

                  <Box display="flex" flexWrap="wrap" gap={2} justifyContent="flex-start">
                    {kpis.map(({ functionalId, empId, kpiName, achievement, target }) => (
                      <Box
                        key={functionalId + "_" + empId}
                        sx={{
                          width: "100%",
                          maxWidth: 220, // or any fixed width for consistency
                          flexGrow: 1,
                        }}
                      >
                        <Stack spacing={0.5}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={
                              <Tooltip title={kpiName}>
                                <span
                                  style={{
                                    display: "inline-block",
                                    maxWidth: 180,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    verticalAlign: "bottom",
                                  }}
                                >
                                  {kpiName}
                                </span>
                              </Tooltip>
                            }
                            value={achievements[functionalId + "_" + empId] ?? achievement ?? ""}
                            onChange={(e) =>
                              handleAchievementChange(functionalId + "_" + empId, e.target.value)
                            }
                            onBlur={(e) => handleSaveAchievement(e, functionalId + "_" + empId)}
                            inputProps={{ step: "0.01", min: 0 }}
                            sx={{
                              ...numberTextFieldStyling,
                              "& .MuiOutlinedInput-root": {
                                ...(!!(achievements[functionalId + "_" + empId] ?? achievement)
                                  ? { "& fieldset": { borderColor: "green" } }
                                  : {}),
                              },
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {t("user.functional.target")}: {target}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Paper>
          ))
        : paginatedKPIs.map(([kpiName, employees]) => (
            <Paper key={kpiName} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" mb={1}>
                {kpiName}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={2} justifyContent="flex-start">
                {employees.map(
                  ({ functionalId, empId, empName, jobTitle, achievement, target, image }) => (
                    <Box
                      key={functionalId + "_" + empId}
                      sx={{
                        width: "100%",
                        maxWidth: 240,
                        flexGrow: 1,
                      }}
                    >
                      <Stack spacing={0.5}>
                        {/* Avatar + Name + Job Title */}
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            src={image ? `data:image/jpeg;base64,${image}` : undefined}
                            alt={empName}
                            sx={{ width: 32, height: 32 }}
                          />
                          <Tooltip title={`${empName} (${jobTitle})`}>
                            <Typography
                              variant="subtitle2"
                              noWrap
                              sx={{
                                maxWidth: 160,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {empName}
                            </Typography>
                          </Tooltip>
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            maxWidth: 160,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t("admin.strategicInfo.initiatives.jobTitle")}: {jobTitle}
                        </Typography>
                        {/* Achievement Input */}
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={achievements[functionalId + "_" + empId] ?? achievement ?? ""}
                          onChange={(e) =>
                            handleAchievementChange(functionalId + "_" + empId, e.target.value)
                          }
                          onBlur={(e) => handleSaveAchievement(e, functionalId + "_" + empId)}
                          inputProps={{ step: "0.01", min: 0 }}
                          sx={{
                            maxWidth: 200,
                            ...numberTextFieldStyling,
                            "& .MuiOutlinedInput-root": {
                              ...(!!(achievements[functionalId + "_" + empId] ?? achievement)
                                ? { "& fieldset": { borderColor: "green" } }
                                : {}),
                            },
                          }}
                        />

                        {/* Target info */}
                        <Typography variant="caption" color="text.secondary">
                          {t("user.functional.target")}: {target}
                        </Typography>
                      </Stack>
                    </Box>
                  )
                )}
              </Box>
            </Paper>
          ))}

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={Math.ceil((view === "byJob" ? totalJobGroups : totalKPIGroups) / rowsPerPage)}
          page={page + 1} // Pagination is 1-based, your state is 0-based
          onChange={(e, newPage) => setPage(newPage - 1)}
          color="primary"
        />
      </Box>
      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
}
